import { useQuery } from "@tanstack/react-query";
import { apisiseci } from "../../api";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

export interface Orgao {
    id: number;
    nome: string;
    sigla: string;
    can_remove: boolean;
}

export interface Profile {
    id: number;
    name: string;
    display_name: string;
    can_remove?: boolean;
    orgaos?: Orgao[];
    has_multiple_orgaos?: boolean;
}

export interface AvailableProfilesResponse {
    available_profiles: Profile[];
}

export interface UserProfilesResponse {
    profiles: Profile[];
}

/**
 * Hook para obter perfis disponíveis para o usuário logado
 */
export function useAvailableProfiles() {
    const token = localStorage.getItem("gov_access_token_sso");

    return useQuery<AvailableProfilesResponse, AxiosError>({
        queryKey: ["available-profiles"],
        queryFn: async () => {
            if (!token) throw new Error("Token de autenticação não encontrado.");
            const response = await apisiseci.get<AvailableProfilesResponse>(
                "/profile-management/available-profiles/",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || "Erro ao carregar perfis disponíveis!");
            console.log(error);
        },
    });
}

/**
 * Hook para obter perfis de um usuário específico
 */
export function useUserProfiles(userId: number) {
    const token = localStorage.getItem("gov_access_token_sso");

    return useQuery<UserProfilesResponse, AxiosError>({
        queryKey: ["user-profiles", userId],
        queryFn: async () => {
            if (!token) throw new Error("Token de autenticação não encontrado.");
            const response = await apisiseci.get<UserProfilesResponse>(
                `/profile-management/user-profiles/?user_id=${userId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
        enabled: !!userId,
        onError: (error) => {
            toast.error(error?.response?.data?.detail || "Erro ao carregar perfis do usuário!");
            console.log(error);
        },
    });
}

/**
 * Hook para adicionar perfil a um usuário
 */
export function useAddUserProfile(userId: number) {
    const token = localStorage.getItem("gov_access_token_sso");

    return {
        mutate: async (profileName: string) => {
            if (!token) throw new Error("Token de autenticação não encontrado.");
            const response = await apisiseci.post(
                "/profile-management/add-profile/",
                {
                    user_id: userId,
                    profile_name: profileName,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        },
    };
}

/**
 * Hook para remover perfil de um usuário
 */
export function useRemoveUserProfile(userId: number) {
    const token = localStorage.getItem("gov_access_token_sso");

    return {
        mutate: async (profileName: string, orgaoId?: number) => {
            if (!token) throw new Error("Token de autenticação não encontrado.");
            const response = await apisiseci.delete(
                "/profile-management/remove-profile/",
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    data: {
                        user_id: userId,
                        profile_name: profileName,
                        ...(orgaoId && { orgao_id: orgaoId }),
                    },
                }
            );
            return response.data;
        },
    };
}

